import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, EMPTY, Observable, Subscription } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import memoize from 'lodash.memoize';

import { ButtonPosition, CheckBoxTheme, LoaderTheme, NotificationTheme } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import { Airline, ElementActions, ElementTypes, GaContext, SelectOption } from '@fcom/common/interfaces';
import { ScrollService } from '@fcom/common/services';
import { ValidationErrors } from '@fcom/common/utils';
import { ConfigService, SentryLogger } from '@fcom/core';
import { LoginStatus, Profile, ProfileType } from '@fcom/core-api/login';
import { loginStatus, profile, profileType as selectProfileType } from '@fcom/core/selectors';
import { Amount, CartStatus, PaxDetailsFormData, ServiceSelections } from '@fcom/dapi';
import { asPaxTypeKey } from '@fcom/dapi/utils';
import { finShare, safeMap as safeMapObs, zipWithLastValue } from '@fcom/rx';
import {
  EMAIL_PATTERN,
  FREQUENT_FLIER_AIRLINES,
  getAirlineNameByCode,
  mergeDeep,
  noop,
  rangeFrom,
  trimAndReplaceWhitespace,
  unsubscribe,
  valuesOf,
} from '@fcom/core/utils';
import { Category, ControlData, FinnairCart, FinnairPassengerCode, FinnairPassengerItem } from '@fcom/dapi/api/models';
import { additionalEmailValidator, toAmount } from '@fcom/common-booking/utils/common-booking.utils';
import { PaxFormFields } from '@fcom/common-booking/services/passenger-form.service';
import { formatDateOfBirth } from '@fcom/common-booking/utils/date-utils';
import { TypedFormArray, TypedFormGroup } from '@fcom/service-forms';
import { PassengerDetails, PassengerFormService, PrefillSource } from '@fcom/common-booking';
import { GtmService } from '@fcom/common/gtm';
import { BookingAppState, PaxDetailsState } from '@fcom/common/interfaces/booking';
import { profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors/login.selector';
import { CorporateUser } from '@fcom/common/interfaces/corporate/corporate.interface';
import { CorporateService } from '@fcom/common/services/corporate/corporate.service';
import { isCorporateAdminOrTravelBooker } from '@fcom/common/login/services/utils';

import { indexesOfWantedValue, onlyChangedValues } from '../../../utils';
import { PaxDetailsActions } from '../../../store/actions';
import { AppConstants } from '../../../app-constants';
import {
  additionalEmails as selectAdditionalEmails,
  paxDetails as selectPaxDetails,
  paxDetails,
} from '../../../store/selectors';
import { PaxServiceCategories } from '../interfaces/pax-ancillaries.interface';
import { PaxDetailsMarketingConsentComponent } from './pax-details-marketing-consent/pax-details-marketing-consent.component';
import { setPaxDetails } from '../../../store/actions/pax-details.actions';

const airlineToOption = ({ name, code }: Airline): SelectOption =>
  name && {
    name,
    value: code,
  };

const touchAllControls = (group: UntypedFormGroup) =>
  valuesOf(group.controls).forEach((control: AbstractControl) => {
    if (control instanceof UntypedFormGroup) {
      touchAllControls(control);
    }
    control.markAsTouched();
  });

enum PaxFormStatus {
  INITIAL = 'INITIAL',
  ERROR = 'ERROR',
  VALID = 'VALID',
  INVALID = 'INVALID',
}

/**
 * Offset for scrolling when having errored fields and clicking continue.
 * Accounts for header height and probable input field height.
 */
const ERROR_SCROLL_OFFSET = 150;

@Component({
  selector: 'fin-pax-details-form',
  styleUrls: ['pax-details-form.component.scss'],
  templateUrl: 'pax-details-form.component.html',
})
export class PaxDetailsFormComponent implements OnInit, OnDestroy {
  @Input()
  cartStatus$: Observable<CartStatus>;
  @Input()
  cartData$: Observable<FinnairCart>;
  @Output()
  ready: EventEmitter<{ skipToCheckout: boolean }> = new EventEmitter<{ skipToCheckout: boolean }>();
  @Output()
  infoMessageKey: EventEmitter<string> = new EventEmitter<string>();
  @Output()
  unlockTravelingWith: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('marketingConsent') marketingConsent: PaxDetailsMarketingConsentComponent;

  public readonly C = AppConstants;
  public readonly FIELDS = PaxFormFields;
  public readonly CART_STATUS = CartStatus;
  public readonly ButtonPosition = ButtonPosition;
  public readonly TRACKING_CONTEXT = GaContext.BOOKING_FLOW;

  readonly CheckBoxTheme = CheckBoxTheme;
  readonly LoaderTheme = LoaderTheme;
  readonly NotificationTheme = NotificationTheme;
  readonly PaxFormStatus = PaxFormStatus;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  totalPrice$: Observable<Amount>;
  totalPoints$: Observable<number>;
  priceUpdating$: Observable<boolean>;

  paxForm: TypedFormGroup<UntypedFormArray | UntypedFormGroup | UntypedFormControl>;
  typeLocalizationKeys: string[];
  availableDays: { days$: Observable<number[]>; months: number[]; years: number[] }[] = [];

  selectableLeadPassengers$: Observable<SelectOption[]> = EMPTY;
  phonePrefixes$: Observable<SelectOption[]>;
  frequentFlyerAirlines$: Observable<SelectOption[]>[] = [];

  infantTravelingWithLocked$: Observable<boolean>;

  loginStatus$: Observable<LoginStatus>;
  profileType$: Observable<ProfileType>;
  profile$: Observable<Profile>;

  controlData$: Observable<ControlData>;

  paxExpandersOpen: boolean[];
  corporateExpanderOpen: boolean;
  paxAllowErrorsVisible: Record<string, boolean> = {};
  paxSummary$: Observable<string[]>;

  paxServices$: Observable<PaxServiceCategories>;
  paxSelections$: Observable<ServiceSelections>;
  servicesReady$: Observable<boolean>;
  additionalEmails$: Observable<string[]>;
  emailsArray: string[] = [];
  enableAdditionalEmailInBookingFlow = false;
  paxFormReady$ = new BehaviorSubject(false);
  corporateUsers$: Observable<CorporateUser[]>;
  selectedCorporateUsers$ = new BehaviorSubject<CorporateUser[]>([]);

  loading = false;

  private subscription = new Subscription();
  private timeout: ReturnType<typeof setTimeout>;
  private defaultTravelingWithValue: string[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private store$: Store<BookingAppState>,
    private languageService: LanguageService,
    private scrollService: ScrollService,
    private elementRef: ElementRef,
    private sentryLogger: SentryLogger,
    private gtmService: GtmService,
    private configService: ConfigService,
    private passengerFormService: PassengerFormService,
    private corporateService: CorporateService
  ) {}

  ngOnInit(): void {
    this.loginStatus$ = this.store$.pipe(loginStatus());
    this.profileType$ = this.store$.pipe(selectProfileType());
    this.profile$ = this.store$.pipe(profileOrUndefinedIfNotLoggedIn(), finShare());
    this.controlData$ = this.cartData$.pipe(map((cart) => cart.controlData));

    this.additionalEmails$ = this.store$.pipe(take(1), selectAdditionalEmails());
    this.enableAdditionalEmailInBookingFlow = this.configService.cfg.enableAdditionalEmailInBookingFlow;

    this.setupPaxDetailsFields();
    this.fillInCorporateCodeIfSet();
    this.removeExcessPrefillPassengers();
    this.setUpPaxSummaryText();

    this.paxExpandersOpen = new Array(this.passengers.length).fill(false);
    // Main passenger expander should be open by default
    this.paxExpandersOpen[0] = true;

    this.totalPrice$ = this.cartData$.pipe(
      map((data: FinnairCart) => data?.prices),
      safeMapObs((prices) => {
        return toAmount(prices.unpaid.total.total.totalAmount);
      })
    );
    this.totalPoints$ = this.cartData$.pipe(
      filter((data: FinnairCart) => !!data?.prices?.unpaid?.total?.total?.totalPoints),
      map((data) => +data.prices.unpaid.total.total.totalPoints.amount)
    );
    this.priceUpdating$ = this.cartStatus$.pipe(map((status: CartStatus) => status !== CartStatus.OK));

    this.paxForm.get('passengers')['controls'].forEach((group: FormGroup, i: number) => {
      if (this.getPaxFormStatus(group).value) {
        touchAllControls(group);
        this.paxAllowErrorsVisible[String(i)] = true;
      }
    });

    this.corporateUsers$ = combineLatest([
      this.corporateService.getUsers().pipe(finShare()),
      this.selectedCorporateUsers$,
    ]).pipe(
      finShare(),
      map(([corporateUsers, selectedUsers]) => {
        return corporateUsers.filter((user) => !selectedUsers.includes(user));
      })
    );

    this.subscription.add(
      this.store$.pipe(selectPaxDetails()).subscribe((paxState: PaxDetailsState) => {
        this.emailsArray = paxState?.additionalEmails || this.emailsArray;

        if (this.emailsArray.length >= 3) {
          this.paxForm.get('additionalEmail').disable();
        } else {
          this.paxForm.get('additionalEmail').enable();
        }
      })
    );

    this.subscription.add(
      combineLatest([this.additionalEmails$, this.profileType$])
        .pipe(take(1))
        .subscribe(([additionalEmails, profileType]) => {
          if (
            profileType === ProfileType.CORPORATE ||
            additionalEmails.length > 0 ||
            this.paxForm.get('corporateCode').value
          ) {
            this.corporateExpanderOpen = true;
          }
        })
    );
  }

  /**
   * Wait for a user login when on the pax details page. We detect this by seeing if the login
   * status changes from pending to logged in. When it is detected then try and fill the
   * paxForm.
   */
  setUpLoginListener(passengers: PassengerDetails[]): void {
    this.subscription.add(
      this.profile$
        .pipe(
          pairwise(),
          filter(([prev, curr]: [Profile, Profile]) => prev === undefined && !!curr),
          filter(([_, userProfile]: [unknown, Profile]) => !isCorporateAdminOrTravelBooker(userProfile)),
          take(1),
          switchMap(() => this.passengerFormService.prefillData(passengers[0], [PrefillSource.PROFILE]))
        )
        .subscribe(noop)
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }

  /**
   * If a user enters 2 passengers information, then they go back to the homepage and re-enter with
   * only 1 pax in the search, then pax 2 will need to be deleted so that it is not sent in cart
   * creation.
   */
  removeExcessPrefillPassengers(): void {
    this.subscription.add(
      combineLatest([this.cartData$, this.store$.pipe(paxDetails())])
        .pipe(take(1))
        .subscribe(([cartData, paxDetails]) => {
          const paxIds = cartData.passengers.map((pax) => pax.id);
          const newPassengers = paxDetails.passengers.filter((passenger) => {
            return paxIds.includes(passenger.travelerId);
          });

          if (newPassengers.length < paxDetails.passengers.length) {
            this.store$.dispatch(
              setPaxDetails({
                passengers: newPassengers,
              })
            );
          }
        })
    );
  }

  paxDetailsReady(skipToCheckout = false): void {
    if (this.paxForm.valid) {
      this.loading = true;

      this.subscription.add(
        this.marketingConsent.setMarketingConsent().subscribe(() => {
          this.loading = false;
          this.ready.emit({ skipToCheckout });
        })
      );
    }
  }

  validateForm(): void {
    this.paxForm.updateValueAndValidity({ emitEvent: true });
    this.passengers.controls.forEach((group: UntypedFormGroup, index) => {
      touchAllControls(group);
      if (group.status === PaxFormStatus.INVALID) {
        this.paxExpandersOpen[index] = true;
      }
    });
    setTimeout(() => this.scrollService.scrollToFirstInvalidInput(this.elementRef, ERROR_SCROLL_OFFSET, true), 0);
  }

  paxTypeLocalizationKey(paxType: FinnairPassengerCode): string {
    return `passenger.${asPaxTypeKey(paxType).toLowerCase()}`;
  }

  paxAgeLocalizationKey(type: string): string {
    return `passenger.age.${type}`;
  }

  isPrimaryPax(group: UntypedFormGroup): boolean {
    return group[PaxFormFields.isPrimaryPax];
  }

  onBlurField(): void {
    this.timeout = setTimeout(() => this.infoMessageKey.emit(''), 100);
  }

  onFocusField(next: string): void {
    clearTimeout(this.timeout);
    this.infoMessageKey.emit(next);
  }

  onUnlockTravelingWith(event: Event): void {
    this.unlockTravelingWith.emit();
    event.preventDefault();
  }

  toggleOpen(passenger: FormGroup, isOpen: boolean, index: number): void {
    this.paxExpandersOpen[index] = !isOpen;

    // When closing the expander, trigger validation
    if (isOpen) {
      this.paxForm.get('passengers').get(String(index)).markAllAsTouched();
      this.paxAllowErrorsVisible[String(index)] = this.getPaxFormStatus(passenger).touched;
    }
  }

  toggleOpenCorporate(isOpen: boolean): void {
    this.corporateExpanderOpen = !isOpen;
  }

  getPaxFormStatus(formGroup: FormGroup): { value: boolean; touched: boolean } {
    const shouldIgnoreValidationCheck = (key: string) =>
      // If value is filled from the infant tab we shouldn't invalidate the whole form for the traveller
      key === PaxFormFields.travelling &&
      [...this.defaultTravelingWithValue, null, undefined].includes(formGroup.get(key)?.value);

    const basicInfo = this.getFieldStatus(
      [
        PaxFormFields.gender,
        PaxFormFields.firstName,
        PaxFormFields.lastName,
        PaxFormFields.email,
        PaxFormFields.travelling,
      ],
      formGroup,
      shouldIgnoreValidationCheck
    );

    const phoneNumber = this.getNestedFieldStatus(
      PaxFormFields.phone,
      [PaxFormFields.phoneNumber, PaxFormFields.phonePrefix],
      formGroup
    );

    const frequentFlyer = this.getNestedFieldStatus(
      PaxFormFields.frequentFlyerCard,
      [PaxFormFields.companyCode, PaxFormFields.cardNumber],
      formGroup
    );

    return {
      touched: basicInfo.touched || phoneNumber.touched || frequentFlyer.touched,
      value: basicInfo.value || phoneNumber.value || frequentFlyer.value,
    };
  }

  getFieldStatus(
    fields: Array<string>,
    formGroup: FormGroup,
    shouldIgnoreCheck?: (key: string) => boolean
  ): { value: boolean; touched: boolean } {
    return {
      touched: fields.some((key) => (shouldIgnoreCheck?.(key) ? false : formGroup.get(key)?.touched)),
      value: fields.some((key) => (shouldIgnoreCheck?.(key) ? undefined : formGroup.get(key)?.value)),
    };
  }

  getNestedFieldStatus(
    parentField: string,
    fields: Array<string>,
    formGroup: FormGroup
  ): { value: boolean; touched: boolean } {
    return {
      touched: fields.some((key) => formGroup.get(parentField)?.get(key)?.touched),
      value: fields.some((key) => formGroup.get(parentField)?.get(key)?.value),
    };
  }

  corporateUserSelected(corporateUser: CorporateUser): void {
    const userList = this.selectedCorporateUsers$.value;
    userList.push(corporateUser);
    this.selectedCorporateUsers$.next(userList);
  }

  corporateUserCleared(corporateUser: CorporateUser): void {
    const userList = this.selectedCorporateUsers$.value;
    const index = userList.indexOf(corporateUser);
    if (index > -1) {
      userList.splice(index, 1);
    }
    this.selectedCorporateUsers$.next(userList);
  }

  private setupPaxDetailsFields() {
    this.subscription.add(
      this.cartData$
        .pipe(
          withLatestFrom(this.store$.pipe(selectPaxDetails())),
          take(1),
          tap(([cartData, paxDetailsSnapshot]: [FinnairCart, PaxDetailsState]) => {
            this.setupPaxFormBase(
              cartData?.otherInformation?.corporateUserInformation?.clientId || paxDetailsSnapshot.corporateCode,
              cartData?.otherInformation?.corporateUserInformation?.clientIdInputDisabled
            );
          }),
          switchMap(([cartData]: [FinnairCart, PaxDetailsState]) => {
            return this.setupTravelers(cartData.passengers);
          })
        )
        .subscribe((passengers: PassengerDetails[]) => {
          this.setupInfantTravelingWithLocking(passengers);
          this.updateStoreFromFormChanges();
          this.triggerInitialValidation();
          /**
           * An immediate update to the store is done to remove any invalid data. For example if
           * the pax types have changed from ADT, ADT to ADT, CHD then we need to remove the old
           * adult from saved data. If we don't they appear in the sidebar until the paxForm is
           * touched.
           */
          this.updatePaxDetailsInStore();
          this.paxFormReady$.next(true);
          this.setUpLoginListener(passengers);
        })
    );
  }

  setUpPaxSummaryText(): void {
    this.paxSummary$ = this.store$.pipe(selectPaxDetails()).pipe(
      map(({ passengers }) => {
        return passengers.map(({ firstName, lastName, birthDate, email, phone, frequentFlyerCard }) => {
          const companyName = getAirlineNameByCode(frequentFlyerCard?.companyCode);
          const hasBirthDate = birthDate?.dateOfBirth && birthDate?.monthOfBirth && birthDate?.yearOfBirth;

          return `
            ${firstName || ''}
            ${lastName || ''}
            ${hasBirthDate ? ' / ' + formatDateOfBirth(birthDate) : ''}
            ${email ? ' / ' + email : ''}
            ${phone?.phoneNumber ? `/ (+${phone.phonePrefix.split('|')[1]}) ${phone.phoneNumber}` : ''}
            ${frequentFlyerCard?.cardNumber ? `/ ${companyName}, ${frequentFlyerCard.cardNumber}` : ''}
          `.trim();
        });
      }),
      finShare()
    );
  }

  private fillInCorporateCodeIfSet() {
    this.subscription.add(
      this.store$
        .pipe(profile())
        .pipe(
          take(1),
          tap((userProfile) => {
            this.overrideCorporateCode(userProfile?.corporate?.clientId || '');
          })
        )
        .subscribe({
          error: (error: unknown) => {
            this.sentryLogger.error('Override paxDetails with profile failed', {
              error,
            });
          },
        })
    );
  }

  private overrideCorporateCode(value: string) {
    this.paxForm.get('corporateCode').setValue(value);
  }

  private setupTravelers(passengers: FinnairPassengerItem[]): Observable<PassengerDetails[]> {
    return this.createTravelerFormGroups(passengers).pipe(
      tap((travelers) => {
        this.setupTravellingWithSelectionsForInfants(travelers);
        this.typeLocalizationKeys = travelers.map((traveler: PassengerDetails) => traveler.typeLocalizationKey);
        this.availableDays = travelers.map((traveler: PassengerDetails) => ({
          days$: traveler.availableDays$,
          months: rangeFrom(1, 12),
          years: traveler.years,
        }));
      })
    );
  }

  private createTravelerFormGroups(passengers: FinnairPassengerItem[]): Observable<PassengerDetails[]> {
    return combineLatest(
      passengers.map((passenger: FinnairPassengerItem) => {
        return this.passengerFormService.createPassengerFormGroup(passenger.id);
      })
    ).pipe(
      switchMap((formGroups) => {
        return this.profile$.pipe(
          take(1),
          switchMap((userProfile) => {
            // Skip prefilling from session and profile if user is corporate admin or travel booker
            const sources = isCorporateAdminOrTravelBooker(userProfile)
              ? [PrefillSource.CART, PrefillSource.SESSION]
              : [PrefillSource.CART, PrefillSource.SESSION, PrefillSource.PROFILE];
            return combineLatest(
              formGroups.map((formGroup) => {
                return this.passengerFormService.prefillData(formGroup, sources);
              })
            );
          })
        );
      }),
      tap((paxDetails) => {
        paxDetails.forEach((formGroup) => {
          this.passengers.push(formGroup.group);
        });

        this.passengers.value.forEach(
          (_, index) => (this.frequentFlyerAirlines$[index] = this.setupAirlinePrefixes(index))
        );
      })
    );
  }

  private setupTravellingWithSelectionsForInfants(travelers: PassengerDetails[]) {
    const { adults, infants } = this.getTravelerTypes(travelers);
    this.setupTravellingWithSelectOptions([...adults]);
    this.setupClearingConflictingTravellingWithSelections(infants);
  }

  private setupClearingConflictingTravellingWithSelections(infants: PassengerDetails[]) {
    const travellingFormGroups: AbstractControl[] = infants.map((infant: PassengerDetails) =>
      infant.group.get(PaxFormFields.travelling)
    );

    const travellingWithPaxIds$: Observable<string[]> = combineLatest(
      travellingFormGroups.map((g) => g.valueChanges.pipe(startWith(g.value), distinctUntilChanged()))
    );

    const clearPreviousConflictingSelections = ([newSelections, previousSelections]: [string[], string[]]) =>
      newSelections
        .map((value, index): [string, number] => [value, index])
        .filter(onlyChangedValues(previousSelections))
        .forEach(([newValue, fieldIndexToChange]) => {
          indexesOfWantedValue(newSelections, fieldIndexToChange, newValue).forEach((i) => {
            if (travellingFormGroups[i].value !== '') {
              travellingFormGroups[i].setValue('');
              travellingFormGroups[i].markAsPristine();
              travellingFormGroups[i].markAsUntouched();
            }
          });
        });

    this.subscription.add(
      travellingWithPaxIds$.pipe(zipWithLastValue([])).subscribe(clearPreviousConflictingSelections)
    );

    this.subscription.add(
      this.selectableLeadPassengers$.pipe(take(1)).subscribe((options: SelectOption[]) => {
        const hasInfant = !!travellingFormGroups[0];
        const hasOnlyOneLeadPax = options.length === 1;
        if (hasInfant && hasOnlyOneLeadPax) {
          travellingFormGroups[0].setValue(options[0].value);
          travellingFormGroups[0].markAsPristine();
          travellingFormGroups[0].markAsUntouched();
        }
        this.defaultTravelingWithValue = options.map((option) => option.value);
      })
    );
  }

  private setupTravellingWithSelectOptions(selectableLeadPassengers: PassengerDetails[]) {
    this.selectableLeadPassengers$ = combineLatest(
      selectableLeadPassengers.map((pax: PassengerDetails) => {
        return pax.group.valueChanges.pipe(
          startWith(pax.group.value),
          distinctUntilChanged(),
          switchMap((ad: PaxDetailsFormData) => {
            return this.languageService
              .translate(
                'paxDetails.travellingWith',
                mergeDeep(ad, {
                  firstName: ad.firstName || '',
                  lastName: ad.lastName || '',
                })
              )
              .pipe(map((name): SelectOption => ({ name, value: ad.id })));
          })
        );
      })
    );
  }

  private setupPaxFormBase(corporateCode: string, disableCorporateCode: boolean) {
    this.paxForm = this.fb.group({
      passengers: this.fb.array([]),
      corporateCode: this.fb.control(
        {
          value: corporateCode || '',
          disabled: disableCorporateCode,
        },
        Validators.compose([Validators.minLength(8), Validators.maxLength(8), Validators.pattern(/[a-zA-Z0-9]{8}/)])
      ),
      additionalEmail: this.fb.control(
        '',
        [Validators.pattern(EMAIL_PATTERN)],
        [additionalEmailValidator(this.additionalEmails$)]
      ),
    }) as TypedFormGroup<TypedFormArray<UntypedFormGroup> | UntypedFormControl>;
  }

  updateStoreFromFormChanges(): void {
    this.subscription.add(
      this.passengers.valueChanges
        .pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)))
        .subscribe(() => {
          this.updatePaxDetailsInStore();
          this.frequentFlyerValueChangeValidation(this.passengers);
        })
    );

    this.subscription.add(
      this.paxForm.get('corporateCode').valueChanges.subscribe((corporateCode: string) => {
        this.store$.dispatch(PaxDetailsActions.setCorporateCode({ corporateCode }));
      })
    );
  }

  updatePaxDetailsInStore(): void {
    // getRawValue contains also disabled fields
    const sanitizedData: PaxDetailsFormData[] = this.passengers.getRawValue().map((paxData: PaxDetailsFormData) => {
      return {
        ...paxData,
        firstName: trimAndReplaceWhitespace(paxData.firstName),
        lastName: trimAndReplaceWhitespace(paxData.lastName),
      };
    });

    this.store$.dispatch(PaxDetailsActions.setPaxDetails({ passengers: sanitizedData }));
  }

  addEmail(): void {
    const addedEmail = this.paxForm.get('additionalEmail').value;

    this.emailsArray.push(addedEmail);
    this.store$.dispatch(PaxDetailsActions.setAdditionalEmails({ additionalEmails: this.emailsArray }));
    this.paxForm.get('additionalEmail').reset();

    this.gtmService.trackElement(
      'additional-email',
      'booking-flow_pax-details',
      ElementTypes.BUTTON,
      '',
      ElementActions.CLICK
    );
  }

  removeEmail(index: number): void {
    this.emailsArray.splice(index, 1);
    this.store$.dispatch(PaxDetailsActions.setAdditionalEmails({ additionalEmails: this.emailsArray }));
  }

  private triggerInitialValidation() {
    setTimeout(() => {
      // Trigger initial validation to get continue button showing if initial values are all valid
      this.paxForm.updateValueAndValidity({ emitEvent: true });
      this.passengers.controls.forEach((group: UntypedFormGroup) => {
        valuesOf(group.controls)
          .filter(
            (control) =>
              control.invalid &&
              control.value !== '' &&
              control.value !== null &&
              !this.defaultTravelingWithValue.includes(control.value)
          )
          .forEach((control: UntypedFormControl) => {
            control.markAsTouched();
          });
      });

      if (this.paxForm.controls.corporateCode.value) {
        this.paxForm.controls.corporateCode.markAsTouched();
      }

      this.frequentFlyerValueChangeValidation(this.passengers);
    });
  }

  private setupAirlinePrefixes(passengerIndex) {
    return this.profileType$.pipe(
      map((pType: ProfileType) =>
        FREQUENT_FLIER_AIRLINES.filter((airline) => {
          return !(airline.name === 'Finnair' && pType === ProfileType.FINNAIR_ACCOUNT && passengerIndex === 0);
        }).map(airlineToOption)
      )
    );
  }

  get passengers(): TypedFormArray<TypedFormGroup<UntypedFormGroup | UntypedFormControl>> {
    return this.paxForm.get('passengers') as TypedFormArray<TypedFormGroup>;
  }

  private getTravelerTypes(travelers: PassengerDetails[]) {
    const getTravelersForType = (type: FinnairPassengerCode) =>
      travelers.filter((traveler: PassengerDetails) => traveler.group.get(PaxFormFields.type).value === type);

    return {
      adults: getTravelersForType(FinnairPassengerCode.ADT),
      childs: getTravelersForType(FinnairPassengerCode.CHD),
      infants: getTravelersForType(FinnairPassengerCode.INF),
    };
  }

  private setupInfantTravelingWithLocking(passengers: PassengerDetails[]) {
    this.infantTravelingWithLocked$ = this.cartData$.pipe(
      safeMapObs((data: FinnairCart) => {
        // Lock changing travelingWith if we there is seats in cart
        return !!data.services.unpaid.find((s) => s.category === Category.SEAT);
      }),
      distinctUntilChanged()
    );

    this.subscription.add(
      combineLatest([this.infantTravelingWithLocked$, this.cartStatus$])
        .pipe(map(([locked, status]: [boolean, CartStatus]) => locked || status === CartStatus.PENDING))
        .subscribe((locked: boolean) => {
          this.getTravelerTypes(passengers).infants.forEach((passenger: PassengerDetails) => {
            const travellingControl = passenger.group.get(PaxFormFields.travelling);

            if (locked) {
              travellingControl.disable();
            } else {
              travellingControl.enable();
            }
          });
        })
    );
  }

  private frequentFlyerValueChangeValidation(passengers: TypedFormArray<UntypedFormGroup>) {
    const getField = (control: AbstractControl, fieldName: string) => control?.get(fieldName)?.value;

    const frequentFlyerCards = passengers.controls.map((p) => p.get(PaxFormFields.frequentFlyerCard)).filter(Boolean);

    frequentFlyerCards.forEach((card) => {
      const isError: boolean = frequentFlyerCards
        .filter((fc) => fc !== card)
        .some((c) => {
          return (
            getField(card, PaxFormFields.cardNumber) === getField(c, PaxFormFields.cardNumber) &&
            getField(card, PaxFormFields.companyCode) === getField(c, PaxFormFields.companyCode) &&
            getField(card, PaxFormFields.cardNumber) !== '' &&
            getField(card, PaxFormFields.companyCode) !== undefined
          );
        });
      if (isError) {
        card.setErrors({ sameCardNumberExists: true });
        if (!card.touched) {
          card.markAsTouched();
        }
      } else {
        card.setErrors(null);
      }
    });
  }

  getBorderClasses = memoize(
    (params: { status: string; expanderIsOpen: boolean; allowErrorsVisible: boolean }): string => {
      const expanderIsClosed = !params.expanderIsOpen;
      const allowErrorsVisible = params.allowErrorsVisible;

      if (expanderIsClosed && params.status === PaxFormStatus.INVALID && !allowErrorsVisible) {
        return 'nordic-blue-50-border untouched';
      } else if (expanderIsClosed && params.status === PaxFormStatus.VALID) {
        return 'nordic-blue-400-border valid';
      } else if (expanderIsClosed && params.status === PaxFormStatus.INVALID && allowErrorsVisible) {
        return 'error-900-border error';
      }
    }
  );

  getCorporateBorderClasses = memoize(
    (params: { corporateExpanderOpen: boolean; errors: ValidationErrors; value: string }): string => {
      const expanderIsClosed = params.corporateExpanderOpen;
      const hasErrors = params.errors;
      const hasValue = params.value;

      if (expanderIsClosed && !hasValue) {
        return 'nordic-blue-50-border untouched';
      } else if (expanderIsClosed && !hasErrors && hasValue) {
        return 'nordic-blue-400-border valid';
      } else if (expanderIsClosed && hasErrors && hasValue) {
        return 'error-900-border error';
      }
    }
  );

  getIconClass = memoize((params: { status: string; expanderIsOpen: boolean; allowErrorsVisible: boolean }): string => {
    if (params.expanderIsOpen) {
      return 'nordic-blue-900-text';
    }

    if (params.status === PaxFormStatus.VALID) {
      return 'nordic-blue-400-text';
    } else if (params.allowErrorsVisible) {
      return 'nordic-blue-900-text';
    } else {
      return 'nordic-blue-50-text';
    }
  });

  getCorporateIconClass = memoize((params: { expanderIsOpen: boolean; errors: ValidationErrors; value: string }) => {
    if (params.expanderIsOpen) {
      return 'nordic-blue-900-text';
    }

    if (params.errors) {
      return 'nordic-blue-900-text';
    } else if (params.value) {
      return 'nordic-blue-400-text';
    } else {
      return 'nordic-blue-50-text';
    }
  });

  handleShortcutToPayClick(): void {
    if (this.paxForm.valid) {
      this.ready.emit({ skipToCheckout: true });
    } else {
      this.validateForm();
    }
  }
}
